import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import { Modal } from "antd";
import { Card, CardBody } from "../../../partials/controls";
import {
  registerUUID,
  clearWhatsappSession,
  getWhatsappByUUIDs,
  getPairingCode,
} from "../../../service/whatsappWebServices";
import { toast } from "react-toastify";

interface RegisteredUUID {
  uuid: string;
  phone: string;
}

interface WhatsAppSessionManagerProps {
  registeredUUIDs: RegisteredUUID[];
  onUpdateUUIDs: (uuids: RegisteredUUID[]) => void;
  isEditMode: boolean;
  engine?: "webjs" | "baileys";
}

// Registration state type
interface RegistrationState {
  isOpen: boolean;
  currentUUID: string;
  qrCode: string;
  phoneNumber: string;
  isRegistering: boolean;
  qrReceived: boolean;
}

// Pairing state type
interface PairingState {
  isOpen: boolean;
  phoneNumber: string;
  loading: boolean;
  result: { success: boolean; code: string } | null;
}

const WhatsAppSessionManager: React.FC<WhatsAppSessionManagerProps> = ({
  registeredUUIDs,
  onUpdateUUIDs,
  engine = "webjs",
}) => {
  // Consolidated registration state
  const [registration, setRegistration] = useState<RegistrationState>({
    isOpen: false,
    currentUUID: "",
    qrCode: "",
    phoneNumber: "",
    isRegistering: false,
    qrReceived: false,
  });

  // Edit session state
  const [editSession, setEditSession] = useState<{
    isOpen: boolean;
    uuid: string;
    phone: string;
  }>({
    isOpen: false,
    uuid: "",
    phone: "",
  });

  // Pairing code state
  const [pairing, setPairing] = useState<PairingState>({
    isOpen: false,
    phoneNumber: "",
    loading: false,
    result: null,
  });

  // Status checking
  const [statusChecking, setStatusChecking] = useState<boolean>(false);
  const [whatsappStatus, setWhatsappStatus] = useState<any[]>([]);

  // Track UUIDs being registered (for cleanup)
  const [pendingUUIDs, setPendingUUIDs] = useState<string[]>([]);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  // Connection error state
  const [connectionError, setConnectionError] = useState<string>("");

  // Use refs to avoid stale closure in EventSource
  const registeredUUIDsRef = useRef(registeredUUIDs);
  const onUpdateUUIDsRef = useRef(onUpdateUUIDs);
  const currentUuidRef = useRef("");

  // Keep refs updated
  useEffect(() => {
    registeredUUIDsRef.current = registeredUUIDs;
    onUpdateUUIDsRef.current = onUpdateUUIDs;
  }, [registeredUUIDs, onUpdateUUIDs]);

  useEffect(() => {
    currentUuidRef.current = registration.currentUUID;
  }, [registration.currentUUID]);

  // Confirm dialog wrapper
  const withConfirmDialog = (callback: Function) => (...args: any[]) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this item?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        callback(...args);
      },
    });
  };

  // Handle delete session
  const handleDelete = (index: number) => {
    const updatedUUIDs = registeredUUIDs.filter((_, i) => i !== index);
    onUpdateUUIDs(updatedUUIDs);
  };

  // Reset registration state
  const resetRegistration = useCallback(() => {
    setRegistration({
      isOpen: false,
      currentUUID: "",
      qrCode: "",
      phoneNumber: "",
      isRegistering: false,
      qrReceived: false,
    });
    setPairing({
      isOpen: false,
      phoneNumber: "",
      loading: false,
      result: null,
    });
    setConnectionError("");
  }, []);

  // Handle cancel registration
  const handleCancelRegistration = () => {
    // Filter out pending UUIDs that haven't been saved
    const updatedUUIDs = registeredUUIDs.filter(
      (item) => !pendingUUIDs.includes(item.uuid)
    );

    if (updatedUUIDs.length !== registeredUUIDs.length) {
      onUpdateUUIDs(updatedUUIDs);
    }

    // Clear pending sessions from backend
    if (pendingUUIDs.length > 0) {
      clearWhatsappSession({ uuids: pendingUUIDs }).catch((err) =>
        console.error("Failed to clear session:", err)
      );
      setPendingUUIDs([]);
    }

    // Close EventSource
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }

    resetRegistration();
  };

  // Handle start registration - registerUUID now returns immediately
  const handleStartRegistration = async () => {
    const sessionId = uuidv4();

    setRegistration((prev) => ({
      ...prev,
      isRegistering: true,
      currentUUID: sessionId,
    }));

    try {
      // registerUUID returns immediately, actual registration continues on backend
      await registerUUID({ uuid: sessionId, engine });
      // Add to pending UUIDs for tracking
      setPendingUUIDs((prev) => [...prev, sessionId]);
    } catch (err) {
      console.error("Error starting registration:", err);
      toast.error("Failed to start registration");
      setRegistration((prev) => ({
        ...prev,
        isRegistering: false,
        currentUUID: "",
      }));
    }
  };

  // Save session after QR registration complete
  const handleSaveSession = () => {
    if (!registration.phoneNumber.trim()) {
      toast.error("Please enter a phone number");
      return;
    }

    const updatedUUIDs = registeredUUIDs.map((item) =>
      item.uuid === registration.currentUUID
        ? { ...item, phone: registration.phoneNumber }
        : item
    );
    onUpdateUUIDs(updatedUUIDs);

    // Remove from pending list as it's now saved
    setPendingUUIDs((prev) =>
      prev.filter((uuid) => uuid !== registration.currentUUID)
    );

    resetRegistration();
  };

  // Update existing session
  const handleUpdateSession = () => {
    const updatedUUIDs = registeredUUIDs.map((item) =>
      item.uuid === editSession.uuid
        ? { ...item, phone: editSession.phone }
        : item
    );
    onUpdateUUIDs(updatedUUIDs);
    setEditSession({ isOpen: false, uuid: "", phone: "" });
  };

  // Refresh session status
  const handleRefresh = useCallback(() => {
    if (registeredUUIDs?.length) {
      setStatusChecking(true);
      getWhatsappByUUIDs({
        uuids: registeredUUIDs.map((x) => x.uuid),
      })
        .then((res: any) => {
          setWhatsappStatus(res?.data || []);
        })
        .catch((err) => {
          console.error("Failed to fetch status:", err);
        })
        .finally(() => setStatusChecking(false));
    }
  }, [registeredUUIDs]);

  // Handle pairing code request
  const handleGetPairingCode = async () => {
    if (!pairing.phoneNumber.trim()) {
      toast.error("Please enter a phone number");
      return;
    }

    setPairing((prev) => ({ ...prev, loading: true, result: null }));

    try {
      const result = await getPairingCode({
        uuid: registration.currentUUID,
        phoneNumber: pairing.phoneNumber,
      });
      setPairing((prev) => ({ ...prev, result, loading: false }));

      // Update registration phone number
      setRegistration((prev) => ({
        ...prev,
        phoneNumber: pairing.phoneNumber,
      }));
    } catch (error) {
      console.error("Error getting pairing code:", error);
      setPairing((prev) => ({
        ...prev,
        result: { success: false, code: "" },
        loading: false,
      }));
      toast.error("Failed to get pairing code");
    }
  };

  // Table columns configuration
  const columns = [
    {
      dataField: "uuid",
      text: "UUID",
      sort: false,
    },
    {
      dataField: "phone",
      text: "Phone",
      sort: false,
    },
    {
      dataField: "status",
      text: "Status",
      sort: false,
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_cell: any, row: any, rowIndex: number) => (
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setEditSession({
                isOpen: true,
                uuid: row.uuid,
                phone: row.phone || "",
              });
            }}
          >
            Edit
          </button>
          <button
            className="btn btn-danger btn-sm ml-2"
            onClick={() => {
              setTimeout(() => {
                withConfirmDialog(handleDelete)(rowIndex);
              }, 100);
            }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Updated registered UUIDs with status
  const updatedRegisteredUUIDs = useMemo(() => {
    return registeredUUIDs?.map((d) => {
      const statusData = whatsappStatus.find((x) => x.uuid === d.uuid);
      const status = statusChecking
        ? "Loading Status..."
        : statusData?.status || "Unknown";
      const starting = statusData?.isStarting ? " (Starting)" : "";

      return {
        ...d,
        status: `${status}${starting}`,
      };
    });
  }, [registeredUUIDs, whatsappStatus, statusChecking]);

  // EventSource for real-time QR updates — open as soon as the dialog is shown
  // so we do not miss qr_received that fires before currentUUID state commits.
  useEffect(() => {
    if (!registration.isOpen) return;

    setConnectionError("");

    const apiUrl =
      process.env.REACT_APP_API_URL || "http://localhost:5001/api/v1/";
    const es = new EventSource(`${apiUrl}whatsapp-events`);

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const uuid = currentUuidRef.current;

      if (data.type === "qr_received" && data.data?.uuid === uuid) {
        setRegistration((prev) => ({
          ...prev,
          qrCode: data.data.qr,
          qrReceived: true,
        }));
      }

      if (data.type === "qr_registered" && data.data?.uuid === uuid) {
        const payload = data.data;
        const eventPhone =
          payload.phone != null && String(payload.phone).trim() !== ""
            ? String(payload.phone).trim()
            : "";
        const canAutoSave =
          payload.success !== false && eventPhone.length > 0;

        const newEntry: RegisteredUUID = {
          uuid: payload.uuid,
          phone: canAutoSave ? eventPhone : "",
        };

        const currentUUIDs = registeredUUIDsRef.current;

        if (!currentUUIDs.some((item) => item.uuid === newEntry.uuid)) {
          onUpdateUUIDsRef.current([...currentUUIDs, newEntry]);
        } else if (eventPhone) {
          onUpdateUUIDsRef.current(
            currentUUIDs.map((item) =>
              item.uuid === newEntry.uuid
                ? { ...item, phone: eventPhone }
                : item
            )
          );
        }

        setPairing({
          isOpen: false,
          phoneNumber: "",
          loading: false,
          result: null,
        });

        if (canAutoSave) {
          setPendingUUIDs((prev) => prev.filter((p) => p !== payload.uuid));
          resetRegistration();
          const displayName =
            payload.name != null && String(payload.name).trim() !== ""
              ? String(payload.name).trim()
              : "";
          toast.success(
            displayName
              ? `WhatsApp session registered (${displayName})`
              : "WhatsApp session registered"
          );
        } else {
          setRegistration((prev) => ({
            ...prev,
            qrCode: "",
            qrReceived: false,
            isRegistering: false,
            phoneNumber: eventPhone,
          }));
        }
      }

      if (data.type === "completed") {
        es.close();
      }
    };

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnectionError(
          "Connection lost. Please close and reopen the dialog to try again."
        );
      }
    };

    setEventSource(es);

    return () => {
      es.close();
    };
  }, [registration.isOpen, resetRegistration]);

  // Refresh status on mount and when UUIDs change
  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  return (
    <Card>
      <CardBody>
        <div className="">
          <div className="d-flex justify-content-between align-items-center">
            <span>
              <b>Register Session</b>
            </span>
            <button
              onClick={handleRefresh}
              className="btn btn-link"
              style={{ fontSize: "2rem" }}
              disabled={statusChecking}
              title="Refresh status"
            >
              &#x21bb;
            </button>
          </div>

          <BootstrapTable
            bootstrap4
            keyField="uuid"
            data={updatedRegisteredUUIDs}
            columns={columns}
            striped
            hover
            condensed
            noDataIndication="No UUIDs registered"
            wrapperClasses="table-responsive"
          />

          <div className="col-lg-8">
            <div className="form-group">
              <button
                className="btn btn-primary btn-lg w-100"
                onClick={() =>
                  setRegistration((prev) => ({ ...prev, isOpen: true }))
                }
              >
                Add Session
              </button>

              {/* Add Session Dialog */}
              {registration.isOpen && (
                <div
                  style={{
                    display: "flex",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1050,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "4px",
                      width: "80%",
                      maxWidth: "500px",
                      padding: "20px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div className="modal-header">
                      <h5 className="modal-title">Register WhatsApp Session</h5>
                      <button
                        type="button"
                        className="close"
                        onClick={handleCancelRegistration}
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>

                    <div className="modal-body">
                      {/* Connection Error Alert */}
                      {connectionError && (
                        <div className="alert alert-danger" role="alert">
                          <strong>Error:</strong> {connectionError}
                        </div>
                      )}

                      {/* Show UUID and phone input after QR is registered */}
                      {registration.currentUUID &&
                        !registration.qrCode &&
                        !registration.isRegistering && (
                          <div className="mb-3">
                            <div className="mb-2">
                              <strong>UUID: </strong>
                              <span
                                style={{
                                  fontSize: "0.9em",
                                  wordBreak: "break-all",
                                }}
                              >
                                {registration.currentUUID}
                              </span>
                            </div>
                            <div className="form-group">
                              <label
                                htmlFor="registrationPhone"
                                className="form-label"
                              >
                                Phone Number:{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                id="registrationPhone"
                                value={registration.phoneNumber || ""}
                                onChange={(e) => {
                                  const value = e?.target?.value || "";
                                  setRegistration((prev) => ({
                                    ...prev,
                                    phoneNumber: value,
                                  }));
                                }}
                                placeholder="Enter phone number"
                              />
                            </div>
                          </div>
                        )}

                      {/* Initial state - show start button */}
                      {!registration.currentUUID &&
                        !registration.isRegistering && (
                          <div>
                            <p>Click the button below to start registration.</p>
                            <button
                              className="btn btn-primary btn-lg w-100"
                              onClick={handleStartRegistration}
                            >
                              Start Registration
                            </button>
                          </div>
                        )}

                      {/* Loading state */}
                      {registration.isRegistering && !registration.qrReceived && (
                        <div className="d-flex justify-content-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <span className="ml-3">Loading...</span>
                        </div>
                      )}

                      {/* QR Code display */}
                      {registration.qrCode && (
                        <div className="text-center">
                          <p>Scan the QR code with WhatsApp:</p>
                          <img
                            key={registration.qrCode}
                            src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
                              registration.qrCode
                            )}&size=256x256`}
                            alt="QR Code"
                            style={{
                              marginTop: "10px",
                              width: "100%",
                              maxWidth: "256px",
                              height: "auto",
                            }}
                          />
                          <p className="mt-2 text-muted small">
                            Or use pairing code instead
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="modal-footer">
                      {registration.qrCode && (
                        <button
                          type="button"
                          className="btn btn-secondary mr-auto"
                          onClick={() =>
                            setPairing((prev) => ({ ...prev, isOpen: true }))
                          }
                        >
                          Use Pairing Code
                        </button>
                      )}

                      {registration.currentUUID &&
                        !registration.qrCode &&
                        !registration.isRegistering && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveSession}
                            disabled={!registration.phoneNumber?.trim()}
                          >
                            Save
                          </button>
                        )}

                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancelRegistration}
                      >
                        {registration.currentUUID &&
                        !registration.qrCode &&
                        !registration.isRegistering
                          ? "Cancel"
                          : "Close"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pairing Code Dialog */}
              {pairing.isOpen && (
                <div
                  style={{
                    display: "flex",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1060,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "4px",
                      width: "80%",
                      maxWidth: "500px",
                      padding: "20px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div className="modal-header">
                      <h5 className="modal-title">Pairing with Code</h5>
                      <button
                        type="button"
                        className="close"
                        onClick={() =>
                          setPairing({
                            isOpen: false,
                            phoneNumber: "",
                            loading: false,
                            result: null,
                          })
                        }
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>

                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">
                          <b>Phone Number:</b>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="pairingPhone"
                          value={pairing.phoneNumber || ""}
                          onChange={(e) => {
                            const value = e?.target?.value || "";
                            setPairing((prev) => ({
                              ...prev,
                              phoneNumber: value,
                            }));
                          }}
                          placeholder="e.g., 852XXXXXXXX"
                          disabled={pairing.loading}
                        />
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleGetPairingCode}
                        disabled={
                          !pairing.phoneNumber.trim() || pairing.loading
                        }
                      >
                        {pairing.loading ? "Loading..." : "Get Pairing Code"}
                      </button>

                      {pairing.result && (
                        <div className="mt-3 p-3 bg-light rounded text-center">
                          <p className="mb-2">
                            <strong>Pairing Code:</strong>
                          </p>
                          {pairing.result.success ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "8px",
                                margin: "15px 0",
                                fontSize: "24px",
                                fontWeight: "bold",
                                fontFamily: "monospace",
                              }}
                            >
                              {pairing.result.code
                                .split("")
                                .map((digit, index) => {
                                  // Add dash separator after 4th digit
                                  if (index === 4) {
                                    return (
                                      <React.Fragment key={`group-${index}`}>
                                        <span
                                          style={{
                                            margin: "0 5px",
                                            color: "#666",
                                          }}
                                        >
                                          -
                                        </span>
                                        <div
                                          key={index}
                                          style={{
                                            width: "40px",
                                            height: "50px",
                                            border: "2px solid #28a745",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "white",
                                            color: "#28a745",
                                          }}
                                        >
                                          {digit}
                                        </div>
                                      </React.Fragment>
                                    );
                                  }
                                  return (
                                    <div
                                      key={index}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        border: "2px solid #28a745",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "white",
                                        color: "#28a745",
                                      }}
                                    >
                                      {digit}
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <h3 className="text-danger">Failed to get code</h3>
                          )}
                          {pairing.result.success && (
                            <p className="text-muted small mt-2">
                              Enter this code in WhatsApp Settings → Linked
                              Devices
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setPairing({
                            isOpen: false,
                            phoneNumber: "",
                            loading: false,
                            result: null,
                          })
                        }
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Session Dialog */}
              {editSession.isOpen && (
                <div
                  style={{
                    display: "flex",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1050,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "4px",
                      width: "80%",
                      maxWidth: "500px",
                      padding: "20px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <div className="modal-header">
                      <h5 className="modal-title">Edit Session</h5>
                      <button
                        type="button"
                        className="close"
                        onClick={() =>
                          setEditSession({ isOpen: false, uuid: "", phone: "" })
                        }
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>

                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">
                          <b>UUID:</b>
                        </label>
                        <div
                          style={{ wordBreak: "break-all", fontSize: "0.9em" }}
                        >
                          {editSession.uuid}
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">
                          <b>Phone Number:</b>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="editSessionPhone"
                          value={editSession.phone || ""}
                          onChange={(e) => {
                            const value = e?.target?.value || "";
                            setEditSession((prev) => ({
                              ...prev,
                              phone: value,
                            }));
                          }}
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUpdateSession}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setEditSession({ isOpen: false, uuid: "", phone: "" })
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default WhatsAppSessionManager;
